import {
	type ComponentType,
	type FC,
	type KeyboardEvent,
	useCallback,
	useEffect,
	useId,
	useMemo,
	useRef,
	useState,
} from "react"
import styled, { css, keyframes } from "styled-components"

import Portal from "@forento/ui/components/Portal"

import { dangerColor } from "~/utilities/styles"

const width = 200

export type DropdownItem = { label: string; onClick(): void; icon?: ComponentType; isDanger?: boolean }
type State = "idle" | "open" | "closed"
type ButtonRef = HTMLButtonElement | HTMLDivElement | HTMLAnchorElement

export default function useDropdownMenu(options: DropdownItem[]) {
	const [state, setState] = useState<State>("idle")
	const [buttonElement, setButtonElement] = useState<ButtonRef | null>(null)
	const menuId = useId()

	useEffect(() => {
		if (state !== "open") return

		const abortController = new AbortController()

		document.addEventListener(
			"click",
			event => {
				if (event.target instanceof Node && buttonElement?.contains(event.target)) return
				setState("closed")
			},
			{ signal: abortController.signal },
		)

		return () => {
			abortController.abort()
		}
	}, [state, buttonElement])

	const close = useCallback(
		(returnFocus: boolean) => {
			setState("closed")
			if (returnFocus) buttonElement?.focus()
		},
		[buttonElement],
	)

	return {
		button: {
			onClick: () => setState(state => (state === "open" ? "closed" : "open")),
			ref: setButtonElement,
			"aria-haspopup": "menu",
			"aria-expanded": state === "open",
			"aria-controls": state === "open" ? menuId : undefined,
		} as const,
		component:
			state !== "idle" ? (
				<Component id={menuId} state={state} options={options} buttonElement={buttonElement} onClose={close} />
			) : null,
	}
}

type Props = {
	id: string
	state: "open" | "closed"
	options: DropdownItem[]
	buttonElement: ButtonRef | null
	onClose(returnFocus: boolean): void
}
const Component: FC<Props> = ({ id, state, options, buttonElement, onClose }) => {
	const itemRefs = useRef<(HTMLButtonElement | null)[]>([])
	const position = useMemo(() => {
		if (buttonElement === null) return { x: 0, y: 0 }
		const bounds = buttonElement.getBoundingClientRect()
		return {
			x: bounds.x + bounds.width / 2 - width / 2,
			y: window.scrollY + bounds.y + bounds.height + 8,
		}
	}, [buttonElement])

	// Follows the WAI-ARIA menu button pattern: focus the first item on open, arrows move, Escape and Tab close. Focus
	// moves from a ref callback because Portal mounts the menu a render after this component.
	const focusFirstItem = useCallback(
		(container: HTMLDivElement | null) => {
			if (container === null || state !== "open" || container.contains(document.activeElement)) return
			container.querySelector<HTMLElement>("[role=menuitem]")?.focus()
		},
		[state],
	)

	const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
		const items = itemRefs.current.filter(item => item !== null)
		if (items.length === 0) return
		const index = items.indexOf(document.activeElement as HTMLButtonElement)
		const nextIndex =
			event.key === "ArrowDown"
				? (index + 1) % items.length
				: event.key === "ArrowUp"
					? (index - 1 + items.length) % items.length
					: event.key === "Home"
						? 0
						: event.key === "End"
							? items.length - 1
							: null
		if (nextIndex !== null) {
			event.preventDefault()
			items[nextIndex]?.focus()
		} else if (event.key === "Escape") {
			event.preventDefault()
			event.stopPropagation()
			onClose(true)
		} else if (event.key === "Tab") {
			// Focus returns to the trigger before the browser handles Tab, so it moves on from there.
			onClose(true)
		}
	}

	if (buttonElement === null) return null

	return (
		<Portal>
			<Container
				ref={focusFirstItem}
				id={id}
				role="menu"
				aria-orientation="vertical"
				inert={state !== "open"}
				onKeyDown={handleKeyDown}
				$state={state}
				$x={position.x}
				$y={position.y}
			>
				{options.map((option, index) => (
					<Option
						key={index}
						ref={element => {
							itemRefs.current[index] = element
						}}
						type="button"
						role="menuitem"
						tabIndex={-1}
						$isDanger={option.isDanger}
						onClick={() => {
							option.onClick()
							onClose(false)
						}}
					>
						{option.icon && <OptionIcon as={option.icon} aria-hidden />}
						{option.label}
					</Option>
				))}
			</Container>
		</Portal>
	)
}

const openAnimation = keyframes`
	from {
		transform: scale(0);
	}
	to {
		transform: scale(1);
	}
`

const closeAnimation = keyframes`
	from {
		transform: scale(1);
	}
	to {
		transform: scale(0);
	}
`

const Container = styled.div<{ $state: State; $x: number; $y: number }>`
	position: absolute;
	width: ${width}px;
	top: ${props => props.$y}px;
	left: ${props => props.$x}px;
	transform: scale(0);
	transform-origin: top center;
	box-shadow: 0 20px 40px -2px rgba(0, 0, 0, 0.16);
	border-radius: 8px;
	overflow: hidden;

	${props =>
		props.$state === "open"
			? css`
					animation: ${openAnimation} 0.15s forwards;
				`
			: props.$state === "closed"
				? css`
						animation: ${closeAnimation} 0.15s forwards;
					`
				: ""}
`

const Option = styled.button<{ $isDanger?: boolean }>`
	cursor: pointer;
	text-align: left;
	width: 100%;
	padding: 12px 24px;
	background-color: white;
	transition: 0.1s background-color;
	display: flex;
	gap: 8px;
	align-items: center;

	${props =>
		props.$isDanger &&
		css`
			color: ${dangerColor};
		`}

	&:hover,
	&:focus-visible {
		background-color: #f2f2f2;
		outline: none;
	}

	&:active {
		background-color: #e6e6e6;
	}
`

const OptionIcon = styled.div`
	width: 24px;
	height: 24px;
`
